import { useMemo, useState } from "react";
import { Link } from "react-router";
import { Plus, Search, Trash2 } from "lucide-react";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import { getTenant, listTenants, purgeTenant } from "@/api/tenants";
import { ConfirmModal } from "@/components/ConfirmModal";
import { Checkbox, Field, Input } from "@/components/form";
import { MenuItem } from "@/components/KebabMenu";
import {
  ErrorBox,
  RefreshFailed,
  isLoadingOrPaused,
  loadFailure,
} from "@/components/Spinner";
import { ReadinessBadge, TenantStatusBadge } from "@/components/StatusBadge";
import { ButtonLink, EmptyState, Page, PageHeader, Table, type TableColumn } from "@/components/ui";
import type { TenantMetadata } from "@/types/api";

const COLUMNS: TableColumn<TenantMetadata>[] = [
  {
    key: "name",
    header: "Name",
    cell: (t) => (
      <>
        <Link to={`/tenants/${t.tenantId}`} className="font-medium text-brand-600 hover:underline">
          {t.displayName}
        </Link>
        <div className="font-mono text-xs text-ink-muted">{t.tenantId}</div>
      </>
    ),
  },
  { key: "domain", header: "Domain", muted: true, cell: (t) => t.domain },
  { key: "status", header: "Status", cell: (t) => <TenantStatusBadge status={t.status} /> },
  {
    key: "login",
    header: "Login",
    cell: (t) => <ReadinessBadge ready={t.authProvisioned && t.cognitoDomainStatus === "active"} />,
  },
];

export function TenantsPage() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [purgeTarget, setPurgeTarget] = useState<TenantMetadata | null>(null);

  // The tenant list doesn't carry stages, so we don't know whether the purge
  // target owns a production one until the modal opens — one request behind
  // the "Delete permanently…" click. While it's in flight (or if it fails) we don't know,
  // and ignorance resolves in favour of the guard: the key stays required
  // until proven unnecessary, never the other way round.
  const purgeStagesQuery = useQuery({
    queryKey: ["tenant", purgeTarget?.tenantId],
    queryFn: () => getTenant(token, purgeTarget!.tenantId),
    enabled: !!token && !!purgeTarget,
  });
  const purgeRequiresKey =
    purgeStagesQuery.data?.stages.some((s) => s.isProduction) ?? true;

  // Active + draft come from the default sweep; archived lives in its own GSI
  // partition so toggling "Afficher les archivés" fires a second query.
  const [activeQuery, archivedQuery] = useQueries({
    queries: [
      {
        queryKey: ["tenants", "active"],
        queryFn: () => listTenants(token),
        enabled: !!token,
      },
      {
        queryKey: ["tenants", "archived"],
        queryFn: () => listTenants(token, "archived"),
        enabled: !!token && showArchived,
      },
    ],
  });

  const isLoading =
    isLoadingOrPaused(activeQuery) ||
    (showArchived && isLoadingOrPaused(archivedQuery));
  // Load errors only: the purge error belongs to the confirmation modal (its
  // ``error`` prop below), not to the page — folded in here it replaced the
  // whole table with an ErrorBox and left it there after "Cancel".
  // And only an error with NOTHING to show empties the table (``loadFailure``): a
  // failed refetch (after a purge, say) keeps the tenants on screen behind a
  // notice, and a failed "archived" query must not take the active tenants
  // down with it.

  const allItems = useMemo(
    () => [
      ...(activeQuery.data?.items ?? []),
      ...(showArchived ? (archivedQuery.data?.items ?? []) : []),
    ],
    [activeQuery.data, archivedQuery.data, showArchived],
  );

  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allItems;
    return allItems.filter((t) =>
      [t.tenantId, t.displayName, t.domain]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q)),
    );
  }, [allItems, search]);

  const qc = useQueryClient();
  const purgeMut = useMutation({
    mutationFn: ({
      tenantId,
      body,
      productionKey,
    }: {
      tenantId: string;
      body: { confirm: string };
      productionKey?: string;
    }) => purgeTenant(token, tenantId, body, productionKey),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
      setPurgeTarget(null);
    },
  });

  return (
    <Page>
      <PageHeader
        title="Tenants"
        actions={
          <>
            <Field label="Search tenants" hideLabel className="w-56">
              <Input
                size="sm"
                type="search"
                icon={<Search />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
              />
            </Field>
            <Checkbox
              label="Show archived"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            <ButtonLink to="/onboarding" variant="primary" icon={Plus}>
              New tenant
            </ButtonLink>
          </>
        }
      />

      <RefreshFailed query={activeQuery} className="mb-4" />
      <RefreshFailed query={archivedQuery} className="mb-4" />
      {showArchived && archivedQuery.isLoadingError && (
        <div className="mb-4">
          <ErrorBox error={archivedQuery.error} />
        </div>
      )}

      <Table
        label="Tenants"
        columns={COLUMNS}
        rows={isLoading ? undefined : items}
        rowKey={(t) => t.tenantId}
        error={loadFailure(activeQuery)}
        empty={
          search ? (
            <EmptyState title="No tenants match your search" />
          ) : (
            <EmptyState
              title="No tenants yet"
              action={
                <ButtonLink to="/onboarding" variant="primary" icon={Plus}>
                  New tenant
                </ButtonLink>
              }
            >
              Create the first one to see it here.
            </EmptyState>
          )
        }
        actions={{
          label: (t) => `Actions for ${t.tenantId}`,
          // Only an archived tenant can be purged: the others have no action.
          items: (t) =>
            t.status === "archived" ? (
              <MenuItem
                danger
                icon={Trash2}
                disabled={purgeMut.isPending && purgeMut.variables?.tenantId === t.tenantId}
                onClick={() => setPurgeTarget(t)}
              >
                Delete permanently…
              </MenuItem>
            ) : null,
        }}
      />

      {purgeTarget && (
        // Stages are fetched behind the click (see purgeStagesQuery above) so
        // the key is only demanded when a production stage is actually in
        // play — asking for it unconditionally would make the operator keep
        // the secret on their clipboard for routine dev-tenant cleanup,
        // which defeats the guard by habit (spec §6). While that fetch is
        // in flight, or if it errors, purgeRequiresKey stays true.
        <ConfirmModal
          title={`Permanently delete "${purgeTarget.displayName}"`}
          confirmLabel="Permanently delete"
          pendingLabel="Deleting…"
          danger
          pending={purgeMut.isPending}
          confirmPhrase={purgeTarget.tenantId}
          productionKey={purgeRequiresKey}
          error={purgeMut.error}
          onCancel={() => {
            purgeMut.reset();
            setPurgeTarget(null);
          }}
          onConfirm={(key) =>
            purgeMut.mutate({
              tenantId: purgeTarget.tenantId,
              body: { confirm: purgeTarget.tenantId },
              productionKey: key,
            })
          }
        >
          <p>
            Deletes the tenant and all its stages. This action{" "}
            <strong>cannot be undone</strong>.
          </p>
        </ConfirmModal>
      )}
    </Page>
  );
}
